/**
 * One transaction, in a list.
 *
 * The same anatomy everywhere it appears — sort inbox, ledger, trash — so the
 * rows stay recognizable and the amount column never moves. Which actions show
 * is the caller's decision; the layout is not.
 */

import type { ReactNode } from 'react';
import {
  allocationsFor,
  flowLabel,
  merchantName,
  shortDate,
  transactionFlowOf,
  amountAdjustment,
  isUnresolvedPending,
  type BudgetIndex,
  type BudgetItem,
  type Transaction,
} from '@alohamula/domain';
import { Badge, Money } from './ui';
import { colorVar } from '../budget/colors';
import './transactions.css';

interface TransactionRowProps {
  transaction: Transaction;
  index: BudgetIndex;
  itemsById: Map<string, BudgetItem>;
  now: Date;
  /** Rendered on a second line so the amount column stays aligned. */
  actions?: ReactNode;
}

export function TransactionRow({
  transaction,
  index,
  itemsById,
  now,
  actions,
}: TransactionRowProps) {
  const flow = transactionFlowOf(transaction);
  const label = flowLabel(flow);
  const allocations = allocationsFor(transaction.id, index);
  const adjustment = amountAdjustment(transaction);
  const stale = isUnresolvedPending(transaction, now);

  return (
    <li className="tx">
      <div className="tx__main">
        <span className="tx__title" title={transaction.description}>
          {merchantName(transaction.description)}
        </span>
        <span className="tx__meta">
          <span>{shortDate(transaction.transactionDate, now)}</span>

          {label && <Badge tone="neutral">{label}</Badge>}

          {transaction.status === 'pending' && (
            <Badge tone={stale ? 'warning' : 'neutral'}>
              {stale ? 'Pending, never posted' : 'Pending'}
            </Badge>
          )}

          {adjustment != null && (
            <Badge tone="warning">
              {adjustment > 0 ? 'Increased' : 'Decreased'} by{' '}
              <Money value={Math.abs(adjustment)} tone="inherit" />
            </Badge>
          )}

          {allocations.length > 1 && <Badge tone="action">Split {allocations.length} ways</Badge>}

          {allocations.map((allocation) => {
            const item = itemsById.get(allocation.budgetItemId);
            if (!item) return null;
            return (
              <span className="tx__filed" key={allocation.id}>
                <span
                  className="dot"
                  style={{ background: colorVar(item.color) }}
                  aria-hidden="true"
                />
                {item.name}
                {allocations.length > 1 && (
                  <>
                    {' · '}
                    <Money value={allocation.amount} tone="inherit" />
                  </>
                )}
              </span>
            );
          })}
        </span>
      </div>

      <Money className="tx__amount" value={transaction.amount} signed flow={flow} />

      {actions && <div className="tx__actions">{actions}</div>}
    </li>
  );
}
